import { createId, findLayer, type EditorDocument } from '../core/model';
import type { DocumentSession } from '../core/session';
import { prepareNativeDocumentResources, releaseNativeDocumentResources } from '../public-format/native-resources';
import { deserializeProject, serializeProject } from './project-format';
import type { RecoverySummary } from './recovery';

// Separate storage leaves the existing committed-document recovery generations intact.
const DATABASE = 'maipo-text-drafts';
const DRAFTS = 'drafts';
const BASES = 'bases';
const MAX_BYTES = 512 * 1024 * 1024;
const MAX_SOURCE_UNITS = 1024 * 1024;
const MAX_RECORDS = 64;
export interface TextDraftRecoverySummary extends RecoverySummary { kind: 'text-draft'; layerId: string; layerName: string; }
interface DraftRecord extends TextDraftRecoverySummary { value: string; archiveBytes: number; }
export type TextDraftRecoveryState = { status: 'idle' | 'saving' | 'saved' | 'failed'; message: string };
function result<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => { request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error ?? new Error('Draft storage failed.')); });
}
async function database(): Promise<IDBDatabase> {
  const request = indexedDB.open(DATABASE, 1);
  request.onupgradeneeded = () => {
    request.result.createObjectStore(DRAFTS, { keyPath: 'key' });
    request.result.createObjectStore(BASES);
  };
  return result(request);
}
async function write<T>(db: IDBDatabase, work: (tx: IDBTransaction) => T | Promise<T>): Promise<T> {
  const tx = db.transaction([DRAFTS, BASES], 'readwrite');
  const completion = new Promise<void>((resolve, reject) => { tx.oncomplete = () => resolve(); tx.onabort = () => reject(tx.error ?? new Error('Draft storage transaction aborted.')); });
  const observed = completion.then(() => ({ ok: true as const }), (error: unknown) => ({ ok: false as const, error }));
  try {
    const value = await work(tx); const done = await observed;
    if (!done.ok) throw done.error;
    return value;
  } catch (error) {
    try { tx.abort(); } catch { /* already terminal */ }
    await observed; throw error;
  }
}
function validate(record: DraftRecord): void {
  if (!record || record.kind !== 'text-draft' || typeof record.key !== 'string' || typeof record.documentId !== 'string'
    || typeof record.documentName !== 'string' || typeof record.sessionId !== 'string' || typeof record.layerId !== 'string'
    || typeof record.layerName !== 'string' || typeof record.value !== 'string' || record.value.length > MAX_SOURCE_UNITS
    || !Number.isSafeInteger(record.revision) || record.revision < 0 || typeof record.savedAt !== 'string'
    || !Number.isFinite(Date.parse(record.savedAt)) || !Number.isSafeInteger(record.archiveBytes) || record.archiveBytes < 1 || record.archiveBytes > MAX_BYTES) throw new Error('The text draft recovery record is invalid.');
}
export async function listTextDraftRecoveries(): Promise<TextDraftRecoverySummary[]> {
  const db = await database();
  try {
    const records = await result(db.transaction(DRAFTS).objectStore(DRAFTS).getAll()) as DraftRecord[];
    return records.map(record => { validate(record); return { key: record.key, sessionId: record.sessionId, documentId: record.documentId, documentName: record.documentName, revision: record.revision, savedAt: record.savedAt, generation: record.generation, kind: record.kind, layerId: record.layerId, layerName: record.layerName }; }).sort((a, b) => b.savedAt.localeCompare(a.savedAt));
  } finally { db.close(); }
}
export async function deleteTextDraftRecovery(key: string): Promise<void> {
  const db = await database();
  try { await write(db, tx => { tx.objectStore(DRAFTS).delete(key); tx.objectStore(BASES).delete(key); }); }
  finally { db.close(); }
}
export async function loadTextDraftRecovery(key: string): Promise<{ document: EditorDocument; layerId: string; value: string }> {
  const db = await database();
  let candidate: EditorDocument | undefined; let delivered = false;
  try {
    const tx = db.transaction([DRAFTS, BASES]);
    const [record, bytes] = await Promise.all([result(tx.objectStore(DRAFTS).get(key)), result(tx.objectStore(BASES).get(key))]) as [DraftRecord | undefined, Uint8Array | undefined];
    if (!record || !bytes) throw new Error('The text draft recovery no longer exists.');
    validate(record);
    if (!(bytes instanceof Uint8Array) || bytes.byteLength !== record.archiveBytes) throw new Error('The text draft document snapshot is invalid.');
    candidate = (await deserializeProject(bytes)).document;
    const layer = findLayer(candidate.root, record.layerId);
    if (candidate.id !== record.documentId || layer?.kind !== 'text' || layer.text.schemaVersion !== 2) throw new Error('The text draft does not match its starting document.');
    await prepareNativeDocumentResources(candidate);
    candidate.source = { kind: 'recovery', name: record.documentName };
    delivered = true;
    return { document: candidate, layerId: layer.id, value: record.value };
  } finally { if (candidate && !delivered) releaseNativeDocumentResources(candidate); db.close(); }
}

/** Owns an immutable base archive and latest logical source, including invalid
 * input. Writes are serialized; explicit discard waits out any pending write. */
export class TextDraftRecoveryWriter {
  readonly key = createId('text-draft');
  readonly #summary: Omit<TextDraftRecoverySummary, 'savedAt'>;
  readonly #base: Promise<{ bytes: Uint8Array } | { error: unknown }>;
  readonly #original: string;
  readonly #replacedKey: string | undefined;
  #value: string;
  #timer: ReturnType<typeof setTimeout> | undefined;
  #queue: Promise<void> = Promise.resolve();
  #writing: Promise<void> | undefined;
  #closed = false;
  #detached = false;
  #stored = false;
  #sequence = 0;
  #onState: (state: TextDraftRecoveryState) => void;
  constructor(session: DocumentSession, layerId: string, onState: (state: TextDraftRecoveryState) => void, replacedKey?: string) {
    const captured = session.captureRevision(); const layer = findLayer(captured.document.root, layerId);
    if (layer?.kind !== 'text' || layer.text.schemaVersion !== 2) throw new Error('Select portable text to recover.');
    this.#original = layer.text.value; this.#value = this.#original; this.#onState = onState; this.#replacedKey = replacedKey;
    this.#summary = { key: this.key, sessionId: captured.sessionId, documentId: captured.document.id, documentName: captured.document.name, revision: captured.revision, generation: 'current', kind: 'text-draft', layerId, layerName: layer.name };
    // serializeProject captures the base synchronously, before later document edits.
    this.#base = serializeProject(captured.document).then(async bytes => {
      if (bytes.byteLength > MAX_BYTES) throw new Error('This document exceeds the text draft recovery budget.');
      await deserializeProject(bytes);
      return { bytes };
    }).catch((error: unknown) => ({ error }));
  }
  update(value: string): void {
    if (this.#closed || this.#detached) return;
    this.#value = value; this.#sequence++;
    clearTimeout(this.#timer);
    this.#onState({ status: 'saving', message: 'Saving text draft locally…' });
    this.#timer = setTimeout(() => { this.#timer = undefined; void this.flush(); }, 400);
  }
  flush(): Promise<void> {
    clearTimeout(this.#timer); this.#timer = undefined;
    const sequence = this.#sequence;
    this.#queue = this.#queue.then(async () => {
      if (this.#closed || sequence !== this.#sequence) return;
      if (this.#value === this.#original) {
        await deleteTextDraftRecovery(this.key);
        if (this.#replacedKey) await deleteTextDraftRecovery(this.#replacedKey);
        this.#stored = false;
        if (!this.#closed) this.#onState({ status: 'idle', message: '' });
        return;
      }
      if (this.#value.length > MAX_SOURCE_UNITS) throw new Error('The text draft exceeds the local source recovery limit. Copy the text before closing.');
      const base = await this.#base;
      if ('error' in base) throw base.error;
      if (this.#closed || sequence !== this.#sequence) return;
      const record: DraftRecord = { ...this.#summary, savedAt: new Date().toISOString(), value: this.#value, archiveBytes: base.bytes.byteLength };
      validate(record);
      const db = await database();
      try {
        if (this.#closed || sequence !== this.#sequence) return;
        const writing = write(db, async tx => {
          const store = tx.objectStore(DRAFTS);
          const records = await result(store.getAll()) as DraftRecord[];
          if (this.#closed || sequence !== this.#sequence) return;
          const others = records.filter(item => item.key !== this.key && item.key !== this.#replacedKey);
          const bytes = others.reduce((total, item) => total + item.archiveBytes + item.value.length * 2, 0) + record.archiveBytes + record.value.length * 2;
          if (others.length >= MAX_RECORDS || bytes > MAX_BYTES) throw new Error('Text draft recovery is full. Delete older drafts in Recover recent work, or copy this text before closing.');
          // Do not evict older work silently. A replacement is removed only in
          // the transaction that durably stores its new owner and source.
          if (!this.#stored || !records.some(item => item.key === this.key)) tx.objectStore(BASES).put(base.bytes, this.key);
          store.put(record);
          if (this.#replacedKey) { store.delete(this.#replacedKey); tx.objectStore(BASES).delete(this.#replacedKey); }
        });
        this.#writing = writing;
        try { await writing; } finally { if (this.#writing === writing) this.#writing = undefined; }
        if (this.#closed || sequence !== this.#sequence) return;
        this.#stored = true;
        this.#onState({ status: 'saved', message: 'Text draft saved locally · Apply to update the document' });
      } finally { db.close(); }
    }).catch(reason => {
      if (!this.#closed) this.#onState({ status: 'failed', message: reason instanceof Error ? reason.message : 'Text draft recovery failed. Copy the text before closing.' });
    });
    return this.#queue;
  }
  /** Unexpected unmount preserves the latest input; explicit Apply/Cancel discards. */
  detach(): void {
    if (this.#detached) return;
    this.#detached = true; this.#onState = () => undefined;
    if (!this.#closed && this.#value !== this.#original) void this.flush();
    else clearTimeout(this.#timer);
  }
  async discard(): Promise<void> {
    this.#closed = true; clearTimeout(this.#timer);
    // Base serialization cannot publish after #closed; only an already-started
    // storage transaction must finish before deletion. Cancel need not await shaping.
    await this.#writing?.catch(() => undefined);
    await deleteTextDraftRecovery(this.key);
    if (this.#replacedKey) await deleteTextDraftRecovery(this.#replacedKey);
  }
}
